"use client";

import { useState, useEffect, useCallback } from "react";
import { Menu } from "react-aria-components";
import { SkeletonBlock, Text } from "@zapier/design-system";
import type { GlobalSearchEmitFunction } from "../types";
import {
  useSearchDropdownData,
  type SearchDropdownData,
} from "../hooks/useSearchDropdownData";
import { getSearchHistory, clearSearchHistory } from "../hooks/searchHistory";
import {
  GlobalSearchErrorNotice,
  type AssetSearchErrorMessages,
} from "./GlobalSearchErrorNotice";
import { SearchResults } from "./SearchResults";
import { SearchHistory } from "./SearchHistory";
import { RecentlyUpdatedList } from "./RecentlyUpdatedList";
import { Guidepost } from "./Guidepost";
import { AssetSearchSkeleton } from "./AssetSearchSkeleton";
import styles from "./SearchDropdown.module.css";

export interface GlobalSearchDropdownProps {
  /** Optional translated error text; defaults to English. */
  assetSearchErrorMessages?: AssetSearchErrorMessages;
  /** Current search query (consumer controls the input). */
  query: string;
  /**
   * Whether the dropdown is visible.
   * @deprecated This prop is no longer used but is retained for API compatibility.
   */
  isOpen?: boolean;
  /**
   * Callback to close the dropdown.
   * @deprecated This prop is no longer used but is retained for API compatibility.
   */
  onClose?: () => void;
  /** Called when a prompt suggestion is selected -- consumer should update input. */
  onPromptClick?: (prompt: string) => void;
  /**
   * Optional label overrides for prompt categories.
   * @deprecated This prop is no longer used but is retained for API compatibility.
   */
  promptLabels?: Record<string, string>;
  /** Emit function for analytics. */
  emit?: GlobalSearchEmitFunction;
  /**
   * Recent search history. When provided, the component is controlled and the
   * parent owns the history state. React Aria caches items in the menu
   * collection, so mutating history from local state does not rebuild it;
   * lifting the state to the parent does. When omitted, the component falls
   * back to reading and managing history internally.
   */
  history?: string[];
  /** Clears the recent search history. Falls back to internal clearing when omitted. */
  onClearHistory?: () => void;
  /** Injected: this package cannot read the host's flags. */
  includeNextGenZaps?: boolean;
}

/** A skeleton section rendered outside the Menu during loading. */
function SkeletonSection({ title, count }: { title: string; count: number }) {
  return (
    <div className={styles.section}>
      <Text tag="h2" type="Body2" color="TextWeakest" margin="0 0 4px 0">
        {title}
      </Text>
      <AssetSearchSkeleton count={count} />
    </div>
  );
}

/**
 * The dropdown with its own search state, for consumers that mount it
 * themselves. `GlobalSearchInput` renders `GlobalSearchDropdownView` instead,
 * so the state outlives the popover it unmounts on close.
 */
export function GlobalSearchDropdown(props: GlobalSearchDropdownProps) {
  const data = useSearchDropdownData({
    query: props.query,
    emit: props.emit,
    includeNextGenZaps: props.includeNextGenZaps,
  });

  return <GlobalSearchDropdownView {...props} data={data} />;
}

export interface GlobalSearchDropdownViewProps extends Omit<
  GlobalSearchDropdownProps,
  "includeNextGenZaps"
> {
  /** Search state, owned by whoever mounts the dropdown. */
  data: SearchDropdownData;
}

export function GlobalSearchDropdownView({
  query,
  onPromptClick,
  emit,
  history: controlledHistory,
  onClearHistory,
  data,
  assetSearchErrorMessages,
}: GlobalSearchDropdownViewProps) {
  const {
    debouncedQuery,
    searchResults,
    guidepost,
    searchSections,
    indexesWithResults,
    loadingPorter,
    porterResolvedQuery,
    assetData,
    loadingAssets,
    assetError,
    retryAssets,
    recentlyUpdated,
    loadingRecentlyUpdated,
  } = data;

  // When `history` is passed the component is controlled by the parent; otherwise
  // it manages its own history state.
  const isControlled = controlledHistory !== undefined;
  const [internalHistory, setInternalHistory] = useState<string[]>(() =>
    getSearchHistory(),
  );
  const history = controlledHistory ?? internalHistory;

  useEffect(() => {
    if (isControlled) return;
    if (query.length === 0) setInternalHistory(getSearchHistory());
  }, [isControlled, query]);

  const handleClearHistory = useCallback(() => {
    if (onClearHistory) {
      onClearHistory();
      return;
    }
    clearSearchHistory();
    setInternalHistory([]);
  }, [onClearHistory]);

  const porterResolved =
    porterResolvedQuery === debouncedQuery && debouncedQuery.length > 0;
  const hasPorterResults =
    searchResults && (indexesWithResults.length > 0 || guidepost);
  const hasAssetResults = assetData && assetData.totalCount > 0;
  const hasAnyResults = hasPorterResults || hasAssetResults;
  const showingResults = query.length > 0 && hasAnyResults;
  const isSearching = query.length > 0 && (loadingPorter || loadingAssets);

  const hasAssetError =
    assetError !== undefined || (assetData?.failedAssetTypes?.length ?? 0) > 0;
  const showAssetError = query === debouncedQuery && hasAssetError;
  const showResultsBranch =
    showingResults || isSearching || (query.length > 0 && showAssetError);
  const showHistory = query.length === 0 && history.length > 0;

  if (showResultsBranch) {
    return (
      <div className={styles.dropdownPanel}>
        {showAssetError && (
          <div className={styles.section} role="alert" aria-live="polite">
            <GlobalSearchErrorNotice
              messages={assetSearchErrorMessages}
              failedAssetTypes={
                assetError !== undefined
                  ? []
                  : (assetData?.failedAssetTypes ?? [])
              }
              isRetrying={loadingAssets}
              retry={retryAssets}
            />
          </div>
        )}
        {loadingAssets && !hasAssetResults && !hasAssetError ? (
          <>
            <SkeletonSection title="Zap workflows" count={3} />
            <SkeletonSection title="Tables" count={2} />
          </>
        ) : (
          !hasAssetResults &&
          !hasAssetError && (
            <div className={styles.section}>
              <Text
                tag="h2"
                type="Body2"
                color="TextWeakest"
                margin="0 0 4px 0"
              >
                Assets
              </Text>
              <Text type="SmallPrint1Multiline" color="TextWeakest">
                No assets match &ldquo;{debouncedQuery}&rdquo;
              </Text>
            </div>
          )
        )}
        <Menu
          aria-label="Search results"
          className={styles.menu}
          shouldFocusWrap={true}
        >
          {guidepost && (
            <Guidepost
              guidepost={guidepost}
              query={debouncedQuery}
              emit={emit}
            />
          )}
          <SearchResults
            query={debouncedQuery}
            searchSections={searchSections}
            assetData={assetData}
            porterResolved={porterResolved}
            emit={emit}
          />
        </Menu>
        {!porterResolved && (
          <>
            <div className={styles.sectionHorizontal}>
              <Text
                tag="h2"
                type="Body2"
                color="TextWeakest"
                margin="0 0 4px 0"
              >
                Apps
              </Text>
              <SkeletonBlock height="36px" width="120px" />
              <SkeletonBlock height="36px" width="120px" />
            </div>
            <div className={styles.section}>
              <Text
                tag="h2"
                type="Body2"
                color="TextWeakest"
                margin="0 0 4px 0"
              >
                Templates
              </Text>
              <SkeletonBlock height="36px" width="100%" />
              <SkeletonBlock height="36px" width="100%" />
            </div>
          </>
        )}
      </div>
    );
  }

  if (showHistory) {
    return (
      <div className={styles.dropdownPanel}>
        <div className={styles.historyHeader}>
          <Text tag="h2" type="Body2" color="TextWeakest">
            Recent searches
          </Text>
        </div>
        <Menu
          aria-label="Recent searches and recently updated assets"
          className={styles.menu}
          shouldFocusWrap={true}
        >
          <SearchHistory
            history={history}
            onSelect={(entry) => onPromptClick?.(entry)}
            onClearHistory={handleClearHistory}
          />
          {!loadingRecentlyUpdated && (
            <RecentlyUpdatedList items={recentlyUpdated} emit={emit} />
          )}
        </Menu>
        {loadingRecentlyUpdated && (
          <SkeletonSection title="Recently updated" count={3} />
        )}
      </div>
    );
  }

  return (
    <div className={styles.dropdownPanel}>
      {loadingRecentlyUpdated && (
        <SkeletonSection title="Recently updated" count={3} />
      )}
      <Menu
        aria-label="Recently updated assets"
        className={styles.menu}
        shouldFocusWrap={true}
      >
        {!loadingRecentlyUpdated && (
          <RecentlyUpdatedList items={recentlyUpdated} emit={emit} />
        )}
      </Menu>
    </div>
  );
}
